import { useEffect, useMemo } from 'react';
import { arrowLeft, eyes } from '../../assets/images/login';
import { Box, Flex, Img, Input, Text } from '@chakra-ui/react';
import Typography from '../../components_/Typography';
import { Colors } from '../../utils/constants';
import Button from '../../components_/button';
import Dropdown from '../../common/dropdown/Dropdown';
import { useGetAuthOtp, useGetUserSecurityQuestions } from '../../apis/auth';
import { decryptJson } from '../../utils/decryptJson';
import { OptionType } from '../../types';
import customToast, { ToastType } from '../../components_/Toast';
import { useNavigate } from 'react-router-dom';
import { encryptData } from '../../utils/encryptJson';
import { ClipLoader } from 'react-spinners';

type TProps = {
  formik: any;
  email: string;
  token: string;
  isLoading?: boolean;
  setQuestionNumber?: any;
};

const SecurityQuestionForm = ({ formik, email, token, isLoading, setQuestionNumber }: TProps) => {
  const { data, isPending, isSuccess } = useGetUserSecurityQuestions({ email, token });
  const { mutate: getAuthOtp, isPending: getAuthOtpLoading } = useGetAuthOtp();
  const navigate = useNavigate();
  const securityQuestions: OptionType[] = useMemo(() => {
    const obj = data?.data ? decryptJson(data?.data) : {};
    const questions = [];
    for (let i = 1; i <= 3; i++) {
      const questionKey = `question${i}`;

      if (obj?.[questionKey]) {
        questions.push({
          label: obj[questionKey],
          value: obj[questionKey],
          number: i,
        });
      }
    }
    return questions;
  }, [data]);

  useEffect(() => {
    if (isSuccess) {
      const obj = data?.data ? decryptJson(data?.data) : {};
      if (!obj?.question1) {
        customToast(
          'You do not have any security questions set, you will be redirected shortly to the set security questions page',
          ToastType.error,
          '',
          6000,
        );
        setTimeout(() => {
          navigate(`/set-security-questions?token=${token}&source=${email}`);
        }, 7000);
      }
    }
  }, [isSuccess]);
  return (
    <Box bgColor={'white'} px={{ base: '2rem', md: '3.5rem' }} py={'2rem'} borderRadius={'0.5rem'}>
      <Flex
        mb={'2rem'}
        alignItems={'center'}
        gap={'8px'}
        cursor={'pointer'}
        onClick={() => formik.setFieldValue('page', 1)}
      >
        <Img src={arrowLeft} boxSize={{ base: '1rem', md: '1.5rem' }} />
        <Typography color={Colors.green} fontSize={'0.7rem'} fontWeight={500}>
          Back
        </Typography>
      </Flex>
      <Box mb={'2rem'}>
        <Typography color={Colors.black} fontSize={'1.125rem'} fontWeight={700} mb={'1rem'}>
          Security Question
        </Typography>
        <Typography color={Colors.black} fontSize={'0.75rem'} fontWeight={400} mb={'1.5rem'}>
          {`Please provide the answer to one of your security questions to confirm that it is really you`}
        </Typography>
        <Dropdown
          options={securityQuestions}
          placeholder={formik.values.securityQuestion ?? 'Select security question'}
          setSelectedOption={(value: { label: string; value: string; number: string }) => {
            formik.setFieldValue('securityQuestion', value?.label);
            setQuestionNumber(value?.number);
          }}
          borderColor='border-[#D4D9E0] border-[0.5px]'
          placeholderFont='12px'
          placeholderColor='!text-[#212121]'
          isLoading={isPending}
          className='h-[2.875rem] !w-[100%] font-[Poppins]'
        />
        {formik?.touched?.securityQuestion && formik?.errors?.securityQuestion && (
          <Typography color={'red'} fontSize={'11px'} fontWeight={400}>
            {formik?.errors?.securityQuestion}
          </Typography>
        )}

        <Box position={'relative'} mt={'1.5rem'}>
          <Input
            borderRadius={'0.25rem'}
            border={`0.5px solid ${Colors.secondaryGreen300}`}
            placeholder='Enter your answer'
            type={'text'}
            fontSize={'0.75rem'}
            fontWeight={400}
            flex={1}
            height={'2.8125rem'}
            onChange={(e) => formik.setFieldValue('securityAnswer', e.target.value)}
            _placeholder={{
              mt: '-10px',
              color: Colors.black,
            }}
            value={formik.values.securityAnswer}
            px={{ base: '0.75rem', md: '1rem' }}
            _focusVisible={{
              outlineColor: 'none',
              border: `2px solid ${Colors.green}`,
            }}
            fontFamily={'Poppins'}
          />
          {formik?.touched?.securityAnswer && formik?.errors?.securityAnswer && (
            <Typography color={'red'} fontSize={'11px'} fontWeight={400}>
              {formik?.errors?.securityAnswer}
            </Typography>
          )}
        </Box>
      </Box>
      <Button
        width={'100%'}
        onClick={() => formik.handleSubmit()}
        py={{ base: '0.75rem', md: '1rem' }}
        height={{ base: '2.5rem', md: '2.875rem' }}
        text={'RESET PASSWORD'}
        type='submit'
        isLoading={isLoading}
        zIndex={1000}
        position={'relative'}
      />

      <Flex mt={'16px'} gap={'4px'} zIndex={1000} position={'relative'}>
        <Text
          fontSize={'10px'}
          fontFamily={'Poppins'}
          lineHeight={'15px'}
          fontWeight={400}
          color={Colors.black}
        >
          Forgotten the answer?
        </Text>
        <Text
          fontSize={'10px'}
          fontFamily={'Poppins'}
          lineHeight={'15px'}
          fontWeight={500}
          color={Colors.green}
          cursor={getAuthOtpLoading ? 'not-allowed' : 'pointer'}
          _hover={{
            textDecorationLine: 'underline',
          }}
          onClick={() => {
            !getAuthOtpLoading &&
              getAuthOtp(encryptData({ email }), {
                onError: (err: Error) =>
                  customToast(err.message || 'Could not get verification otp', ToastType.error),
                onSuccess: () => navigate(`/verify-otp?token=${token}&source=${email}`),
              });
          }}
        >
          {getAuthOtpLoading && <ClipLoader size={'12px'} className='mr-1' color={Colors.green} />}
          {getAuthOtpLoading ? 'Sending verification otp' : `Reset Security Question`}
        </Text>
      </Flex>
    </Box>
  );
};

export default SecurityQuestionForm;
